import React, { useState, useEffect, useRef, useCallback } from 'react';
import emailjs from '@emailjs/browser';
import Testing from './Testing';
import Projects from './Projects';
import Services from './Services';
import About from './About';
import './Home.css';

/** play() is async; pause/navigation can reject with AbortError or "interrupted" — must be caught */
function isBenignVideoPlayError(err) {
  if (!err) return true;
  const msg = String(err.message || '');
  return (
    err.name === 'AbortError' ||
    err.name === 'NotAllowedError' ||
    /interrupted|request was aborted/i.test(msg)
  );
}

function safePlayVideo(video) {
  if (!video) return Promise.resolve();
  const p = video.play();
  if (!p || typeof p.then !== 'function') return Promise.resolve();
  return p.catch((err) => {
    if (!isBenignVideoPlayError(err)) {
      console.log('Video play error:', err);
    }
  });
}

/** Section ids in document order → top-nav slug (testimonials → Projects). */
const HOME_NAV_SCROLL_SPY = [
  { id: 'home', nav: 'home' },
  { id: 'services', nav: 'services' },
  { id: 'testimonials', nav: 'projects' },
  { id: 'projects', nav: 'projects' },
  { id: 'branches', nav: 'branches' },
  { id: 'testing', nav: 'testing' },
  { id: 'about', nav: 'about' }
];

/** Bottom hero marquee — services strip (duplicated in JSX for seamless loop). */
const HERO_TICKER_SERVICES = [
  'Structural Investigation',
  'Geo-Resistivity',
  'Material Testing',
  'Concrete Testing',
  'Soil Investigation',
  'Pile Testing',
  'Non-Destructive Testing',
  'Ground Improvement',
  'Jet Grouting',
  'Micropiling',
];

/** Four branded Home service tiles — hero photos live in /public (concrete.jpg, soil.jpg, steel.jpg, nondes.jpg). */
function homeServiceHeroSrc(filename) {
  return `${process.env.PUBLIC_URL || ''}/${filename}`;
}

/** Logos under `public/ValuedClients/`. Use public root + encoding so `homepage`/subpath deploys and special filenames resolve correctly. */
function valuedClientLogoSrc(relativePath) {
  const normalized = String(relativePath || '').replace(/^\//, '');
  const encoded = normalized.split('/').map(encodeURIComponent).join('/');
  return `${process.env.PUBLIC_URL || ''}/${encoded}`;
}

/** Reading line = just under fixed navbar (60px mobile / 70px desktop); avoids hard-coded 112px drift. */
function getHomeScrollSpyTriggerY() {
  const nav = document.querySelector('.icon-navbar');
  if (nav) {
    const bottom = nav.getBoundingClientRect().bottom;
    const y = Math.round(bottom) + 10;
    return Math.min(Math.max(y, 52), Math.floor(window.innerHeight * 0.42));
  }
  return 88;
}

/**
 * Last section (document order) whose top edge has crossed the reading line.
 * We do NOT use "bounding box contains Y" — a tall section (e.g. Testing) can still contain the
 * line near the top of the viewport while the user is already viewing the next section (About).
 */
function computeHomeNavSection(triggerY) {
  let navSection = HOME_NAV_SCROLL_SPY[0].nav;
  for (const { id, nav } of HOME_NAV_SCROLL_SPY) {
    const el = document.getElementById(id);
    if (!el) continue;
    const top = el.getBoundingClientRect().top;
    if (top > triggerY) break;
    navSection = nav;
  }
  return navSection;
}

function parseInitialHash() {
  if (typeof window === 'undefined') {
    return { page: 'home', section: 'home', aboutResources: false };
  }
  const raw = window.location.hash.replace('#', '');
  if (!raw || raw === 'home') {
    return { page: 'home', section: 'home', aboutResources: false };
  }
  if (raw === 'resources') {
    return { page: 'about', section: 'about', aboutResources: true };
  }
  const valid = ['services', 'projects', 'testing', 'about'];
  if (valid.includes(raw)) {
    return { page: raw, section: raw, aboutResources: false };
  }
  return { page: 'home', section: 'home', aboutResources: false };
}

/**
 * Embeds load Google’s map UI, which may request maps.googleapis.com. Ad blockers often
 * block those (console: ERR_BLOCKED_BY_CLIENT) — that is expected and not a React bug.
 * We defer setting iframe src until the card is near the viewport to avoid loading all maps at once.
 */
function BranchMapIframe({ src, title }) {
  const wrapRef = useRef(null);
  const [showFrame, setShowFrame] = useState(false);

  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setShowFrame(true);
          io.disconnect();
        }
      },
      { root: null, rootMargin: '100px 0px', threshold: 0 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={wrapRef} className="google-map-container">
      {showFrame ? (
        <iframe
          src={src}
          width="100%"
          height="150"
          style={{ border: 0, borderRadius: '8px' }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title={title}
        />
      ) : (
        <div className="branch-map-placeholder" aria-hidden="true">
          Map preview…
        </div>
      )}
    </div>
  );
}

const SERVICE_LEARN_MORE_ARROW_PATH =
  'M13.376 11.552l-.264-10.44-10.44-.24.024 2.28 6.96-.048L.2 12.56l1.488 1.488 9.432-9.432-.048 6.912 2.304.024z';

function ServiceCardLearnMoreButton({ onClick, children = 'Learn More' }) {
  return (
    <button type="button" onClick={onClick} className="service-btn">
      <span className="service-btn__icon-wrapper">
        <svg
          viewBox="0 0 14 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="service-btn__icon-svg"
          width="10"
          aria-hidden="true"
        >
          <path d={SERVICE_LEARN_MORE_ARROW_PATH} fill="currentColor" />
        </svg>
        <svg
          viewBox="0 0 14 15"
          fill="none"
          width="10"
          xmlns="http://www.w3.org/2000/svg"
          className="service-btn__icon-svg service-btn__icon-svg--copy"
          aria-hidden="true"
        >
          <path d={SERVICE_LEARN_MORE_ARROW_PATH} fill="currentColor" />
        </svg>
      </span>
      {children}
    </button>
  );
}

const initialRoute = parseInitialHash();

const Home = () => {
  const [currentPage, setCurrentPage] = useState(initialRoute.page);
  /** When opening the Services page from a home "Learn More" card, which tab to select */
  const [servicesTargetTab, setServicesTargetTab] = useState(null);
  const handleTargetServiceTabApplied = useCallback(() => {
    setServicesTargetTab(null);
  }, []);

  const [aboutScrollToResources, setAboutScrollToResources] = useState(initialRoute.aboutResources);
  const handleAboutResourcesScrollDone = useCallback(() => {
    setAboutScrollToResources(false);
  }, []);

  const [showScrollTop, setShowScrollTop] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [branchPhotoLightbox, setBranchPhotoLightbox] = useState(null);
  const [showTopNavbar, setShowTopNavbar] = useState(false);
  const prevScrollYRef = useRef(0);
  const currentPageRef = useRef(currentPage);
  const [activeSection, setActiveSection] = useState(initialRoute.section);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  
  // Carousel state
  const [carouselPosition, setCarouselPosition] = useState(0);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    message: ''
  });
  

  const scrollProgressRef = useRef(null);
  const videoRef = useRef(null);

  // Client logos data - all logos from ValuedClients folder with transparent backgrounds
     const clientLogos = [
     "ValuedClients/SS-removebg-preview.png",
     "ValuedClients/exan.png",
     "ValuedClients/firstsolid-removebg-preview.png",
     "ValuedClients/supermix-removebg-preview.png",
     "ValuedClients/teravera-removebg-preview.png",
     "ValuedClients/profriends-removebg-preview.png",
     "ValuedClients/upland-removebg-preview.png",
     "ValuedClients/tristar-removebg-preview.png",
     "ValuedClients/magnificon-removebg-preview.png",
     "ValuedClients/jaro-removebg-preview.png",
     "ValuedClients/infinity-removebg-preview.png",
     "ValuedClients/icon-removebg-preview.png",
     "ValuedClients/CMI-removebg-preview.png",
     "ValuedClients/citicon-removebg-preview.png",
     "ValuedClients/tokwing-removebg-preview.png",
     "ValuedClients/webcor-removebg-preview.png",
     "ValuedClients/mgs-removebg-preview.png",
     "ValuedClients/pap-removebg-preview.png",
     "ValuedClients/jfa-removebg-preview.png",
     "ValuedClients/granby-removebg-preview.png",
     "ValuedClients/bsp-removebg-preview.png",
     "ValuedClients/hyundai-removebg-preview.png",
     "ValuedClients/shimz-removebg-preview.png",
     "ValuedClients/ipm-removebg-preview.png",
     "ValuedClients/hpc-removebg-preview.png",
     "ValuedClients/ccd-removebg-preview.png",
     "ValuedClients/sta.rita-removebg-preview.png",
     "ValuedClients/smcc-removebg-preview.png",
     "ValuedClients/cct-removebg-preview.png",
     "ValuedClients/jcrcc-removebg-preview.png",
     "ValuedClients/borland-removebg-preview.png",
     "ValuedClients/BB-removebg-preview.png",
     "ValuedClients/stonerich-removebg-preview.png",
     "ValuedClients/Filinvest-removebg-preview.png",
     "ValuedClients/FirstBalfours-removebg-preview.png",
     "ValuedClients/MDC-removebg-preview.png",
     "ValuedClients/Megaworld-removebg-preview.png"
   ];

  // Scroll progress effect and navbar visibility
  useEffect(() => {
    let scrollSpyRaf = null;

    const runScrollSpy = () => {
      if (currentPageRef.current !== 'home') return;
      const triggerY = getHomeScrollSpyTriggerY();
      setActiveSection(computeHomeNavSection(triggerY));
    };

    const scheduleScrollSpy = () => {
      if (currentPageRef.current !== 'home') return;
      if (scrollSpyRaf != null) cancelAnimationFrame(scrollSpyRaf);
      scrollSpyRaf = requestAnimationFrame(() => {
        scrollSpyRaf = null;
        runScrollSpy();
      });
    };

    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.body.offsetHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      
      if (scrollProgressRef.current) {
        scrollProgressRef.current.style.strokeDashoffset = 125.6 - (125.6 * scrollPercent) / 100;
      }
      
      // Show/hide scroll to top button
      setShowScrollTop(scrollTop > 300);
      
      // Navbar visibility: hide-on-scroll only on the long home page
      if (currentPage !== 'home') {
        setShowTopNavbar(true);
      } else if (scrollTop <= 100) {
        setShowTopNavbar(true);
      } else if (scrollTop < prevScrollYRef.current) {
        setShowTopNavbar(true);
      } else {
        setShowTopNavbar(false);
      }
      prevScrollYRef.current = scrollTop;

      if (currentPageRef.current === 'home') {
        scheduleScrollSpy();
      }
    };

    const handleResize = () => {
      scheduleScrollSpy();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (scrollSpyRaf != null) cancelAnimationFrame(scrollSpyRaf);
    };
  }, [currentPage]);



  // Scroll-triggered animations for sections - Continuous animation on scroll
  useEffect(() => {
    const observerOptions = {
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('section-visible');
        } else {
          // Reset animation when element leaves viewport
          entry.target.classList.remove('section-visible');
        }
      });
    }, observerOptions);

    // Observe all sections with section-animate class
    const sections = document.querySelectorAll('.section-animate');
    sections.forEach((section) => {
      observer.observe(section);
    });

    return () => {
      sections.forEach((section) => {
        observer.unobserve(section);
      });
    };
  }, [currentPage]);

  // Scroll-triggered animations for branch cards (left/middle/right behaviors) - Continuous animation
  useEffect(() => {
    if (currentPage !== 'home') return;

    const cardObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          } else {
            // Reset animation when element leaves viewport
            entry.target.classList.remove('in-view');
          }
        });
      },
      { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0], rootMargin: '0px 0px -40px 0px' }
    );

    const cards = document.querySelectorAll('.branches-section .branch-card');
    cards.forEach((el) => cardObserver.observe(el));

    return () => {
      cards.forEach((el) => cardObserver.unobserve(el));
    };
  }, [currentPage]);

  useEffect(() => {
    if (!branchPhotoLightbox) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setBranchPhotoLightbox(null);
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      const currentPaddingRight = parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
    }
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [branchPhotoLightbox]);

  useEffect(() => {
    if (!contactModalOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeContactModal();
    };
    document.addEventListener('keydown', onKeyDown);
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      const currentPaddingRight = parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
    }
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.paddingRight = prevBodyPaddingRight;
    };
  }, [contactModalOpen]);


  // Enhanced scroll-based animations for continuous effects
  useEffect(() => {
    const handleScrollAnimations = () => {
      const scrollTop = window.pageYOffset;
      const windowHeight = window.innerHeight;
      
      // Sections with map embeds must NOT use scroll-based opacity: on desktop a tall Branches block
      // rarely lines up with the viewport center, so progress stayed low and the whole section looked washed out.
      const animatedElements = document.querySelectorAll(
        '.section-animate:not(.branches-section), .home-fadein'
      );
      
      animatedElements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const elementTop = rect.top + scrollTop;
        const elementHeight = rect.height;
        
        // Calculate how much of the element is visible
        const elementCenter = elementTop + elementHeight / 2;
        const viewportCenter = scrollTop + windowHeight / 2;
        const distanceFromCenter = Math.abs(elementCenter - viewportCenter);
        const maxDistance = windowHeight;
        
        // Calculate animation progress (0 to 1)
        const animationProgress = Math.max(0, 1 - (distanceFromCenter / maxDistance));
        
        // Apply smooth animation based on scroll position
        if (animationProgress > 0.1) {
          element.classList.add('section-visible');
          const translateY = (1 - animationProgress) * 30;
          const scale = 0.95 + (animationProgress * 0.05);
          
          element.style.setProperty('--scroll-translate-y', `${translateY}px`);
          element.style.setProperty('--scroll-scale', String(scale));
        } else {
          element.classList.remove('section-visible');
        }
      });
    };

    // Throttle scroll events for better performance
    let ticking = false;
    const throttledScrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScrollAnimations();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScrollHandler, { passive: true });
    
    // Initial call
    handleScrollAnimations();

    return () => {
      window.removeEventListener('scroll', throttledScrollHandler);
    };
  }, [currentPage]);

  // Video fade-in effect and auto-play handling
  useEffect(() => {
    if (videoRef.current && currentPage === 'home') {
      // Add a small delay to ensure video is loaded
      const timer = setTimeout(() => {
        videoRef.current.classList.add('visible');
        
        // Reset video to beginning
        videoRef.current.currentTime = 0;
        
        // Try to play video with proper error handling for Brave browser
        const playVideo = async () => {
          const el = videoRef.current;
          if (!el) return;
          try {
            if (el.paused) {
              await el.play();
            }
          } catch (e) {
            if (isBenignVideoPlayError(e)) return;
            console.log('Video play error:', e);
            try {
              el.muted = true;
              el.playsInline = true;
              el.setAttribute('webkit-playsinline', 'true');
              el.setAttribute('playsinline', 'true');
              await el.play();
            } catch (e2) {
              if (isBenignVideoPlayError(e2)) return;
              console.log('Video retry failed:', e2);
              if (videoRef.current) {
                videoRef.current.style.display = 'none';
                videoRef.current.parentElement.style.background = 'linear-gradient(135deg, #FFFFFF 0%, #808080 100%)';
              }
            }
          }
        };
        
        playVideo();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentPage]);

  // Handle video play/pause when page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (videoRef.current) {
        if (document.hidden) {
          // Page is hidden, pause video
          videoRef.current.pause();
        } else {
          if (currentPage === 'home') {
            const v = videoRef.current;
            const p = v && v.play();
            if (p && typeof p.catch === 'function') {
              p.catch((e) => {
                if (isBenignVideoPlayError(e)) return;
                console.log('Video play error:', e);
                if (videoRef.current) {
                  videoRef.current.style.display = 'none';
                  videoRef.current.parentElement.style.background = 'linear-gradient(135deg, #FFFFFF 0%, #808080 100%)';
                }
              });
            }
          }
        }
      }
    };

    // Handle user interaction for Brave browser video play
    const handleUserInteraction = () => {
      if (videoRef.current && currentPage === 'home' && videoRef.current.paused) {
        safePlayVideo(videoRef.current);
      }
    };

    const handlePageShow = (e) => {
      if (e.persisted && videoRef.current && currentPage === 'home') {
        safePlayVideo(videoRef.current);
      }
    };

    const handleFocus = () => {
      if (videoRef.current && currentPage === 'home') {
        safePlayVideo(videoRef.current);
      }
    };

    const handleBlur = () => {
      if (videoRef.current) {
        // Window lost focus, pause video
        videoRef.current.pause();
      }
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    
    // Add user interaction listeners for Brave browser
    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };
  }, [currentPage]);

  // Auto-sliding carousel effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCarouselPosition(prev => {
        // Move one card width to the left (slow sliding)
        const cardWidth = 230; // Width of each card (200px) + gap (30px)
        const newPosition = prev - 1; // Move 1px at a time for smooth sliding
        
        // Create infinite loop by resetting to the beginning of the duplicated set
        if (Math.abs(newPosition) >= cardWidth * clientLogos.length) {
          return 0;
        }
        return newPosition;
      });
    }, 50); // Update every 50ms for smooth movement

    return () => clearInterval(interval);
  }, [clientLogos.length]);

  // Update URL hash when currentPage changes and handle video
  useEffect(() => {
    if (currentPage === 'home') {
      window.location.hash = '';
      setActiveSection('home');
      // Force video restart when switching to home
      if (videoRef.current) {
        // Reset video to beginning and play
        videoRef.current.currentTime = 0;
        videoRef.current.load(); // Reload the video
        setTimeout(() => {
          const v = videoRef.current;
          if (!v) return;
          const p = v.play();
          if (p && typeof p.catch === 'function') {
            p.catch((e) => {
              if (isBenignVideoPlayError(e)) return;
              console.log('Video play error when switching to home:', e);
              if (videoRef.current) {
                videoRef.current.style.display = 'none';
                videoRef.current.parentElement.style.background = 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)';
              }
            });
          }
        }, 200);
      }
    } else {
      window.location.hash = currentPage;
      setActiveSection(currentPage);
      // SPA "pages" share one window — reset scroll so e.g. Services opens at the top, not at home’s scroll depth
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      // Pause video when leaving home
      if (videoRef.current) {
        videoRef.current.pause();
      }
    }
  }, [currentPage]);







  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  /** Navbar: Services / Testing go to the home page sections only (not the full Services/Testing routes). */
  const navigateToHomeSection = useCallback(
    (sectionId, activeNavSection) => {
      const scrollInto = () => {
        setActiveSection(activeNavSection);
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      };
      if (currentPage !== 'home') {
        setIsLoading(true);
        setCurrentPage('home');
        setTimeout(() => {
          scrollInto();
          setTimeout(() => setIsLoading(false), 500);
        }, 100);
      } else {
        scrollInto();
      }
    },
    [currentPage]
  );

  /** Logo / mobile wordmark: go to home and scroll to top (same intent as Home nav). */
  const goToHomeFromLogo = useCallback(() => {
    setCurrentPage('home');
    setActiveSection('home');
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
    document.querySelector('.mobile-menu')?.classList.remove('active');
  }, []);

  // Open contact modal
  const openContactModal = () => {
    setContactModalOpen(true);
  };

  // Close contact modal
  const closeContactModal = () => {
    setContactModalOpen(false);
    setSubmitMessage('');
    setFormData({ name: '', email: '', mobile: '', message: '' });
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate phone number format
  const validatePhoneNumber = (phone) => {
    // Remove all non-digit characters
    const cleanPhone = phone.replace(/\D/g, '');
    
    // Check if it's a valid Philippine mobile number
    // Should start with 09 and be 11 digits total
    if (cleanPhone.length !== 11) {
      return 'Phone number must be 11 digits';
    }
    
    if (!cleanPhone.startsWith('09')) {
      return 'Phone number must start with 09';
    }
    
    return '';
  };

  // Validate form before submission
  const validateForm = () => {
    const errors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Phone validation
    if (!formData.mobile.trim()) {
      errors.mobile = 'Mobile number is required';
    } else {
      const phoneError = validatePhoneNumber(formData.mobile);
      if (phoneError) {
        errors.mobile = phoneError;
      }
    }
    
    // Message validation
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle click outside modal to close
  const handleModalBackdropClick = (e) => {
    if (e.target.id === 'contact-modal') {
      closeContactModal();
    }
  };

  // Handle contact form submission
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
      const toEmail = process.env.REACT_APP_EMAILJS_TO_EMAIL;

      // Initialize EmailJS if not already done
      if (!emailjs.init) {
        emailjs.init(publicKey);
      }

      // Send email using EmailJS
      const result = await emailjs.send(
        serviceId,
        templateId,
        {
          name: formData.name,
          email: formData.email,
          mobile: formData.mobile,
          message: formData.message,
          to_email: toEmail
        },
        publicKey
      );

      console.log('EmailJS result:', result);
      
      if (result.status === 200) {
        setSubmitMessage('Message sent successfully! We\'ll get back to you soon.');
        // Clear form after successful submission
        setFormData({ name: '', email: '', mobile: '', message: '' });
        // Close modal after 3 seconds
        setTimeout(() => {
          closeContactModal();
        }, 3000);
      } else {
        throw new Error(`EmailJS returned status ${result.status}`);
      }
    } catch (error) {
      console.error('Email sending failed:', error);
      setSubmitMessage(`Failed to send message: ${error.message}. Please try again or contact us directly at ${process.env.REACT_APP_EMAILJS_TO_EMAIL}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const branchPhotoFallback = `${process.env.PUBLIC_URL}/bannerservice.png`;
  /** Served from public/Images/branches (sync with Images/branches at project root). */
  const branchImagesDir = `${process.env.PUBLIC_URL}/Images/branches`;

  // Branches data — `photo` null uses fallback only (no missing-asset request)
  const branches = [
    {
      name: 'Carmona Branch',
      location: 'Unit 24 Paseo de Carmona Bldg. Brgy, Carmona, Cavite, Philippines',
      contact: '0993-846-9592',
      city: 'Carmona, Cavite',
      address: 'Unit 24 Paseo de Carmona Bldg. Brgy',
      isMain: true,
      mapLink: 'https://maps.google.com/?q=Geomatixs+Engineering+Carmona+Cavite',
      photo: `${branchImagesDir}/carmona.jpg`
    },
    {
      name: 'Tanza Branch',
      location: '36 A. Sorianoo Brgy. Daang Amaya 2, Tanza, Cavite',
      contact: '0905-328-0253',
      city: 'Tanza, Cavite',
      address: '36 A. Sorianoo Brgy. Daang Amaya 2',
      isMain: false,
      mapLink: 'https://maps.google.com/?q=Geomatixs+Engineering+Tanza+Cavite',
      photo: `${branchImagesDir}/tanza.jpg`
    },
    {
      name: 'Gentri Branch',
      location: 'MMSB Commercial Bldg, Brgy. Santiago Arnaldo Hwy, General Trias, Cavite',
      contact: '0917-831-8552',
      city: 'General Trias, Cavite',
      address: 'MMSB Commercial Bldg, Brgy. Santiago Arnaldo Hwy',
      isMain: false,
      mapLink: 'https://maps.google.com/?q=Geomatixs+Engineering+General+Trias+Cavite',
      photo: `${branchImagesDir}/gentri.jpg`
    },
    {
      name: 'Sto Tomas Branch',
      location: '448R+GHM, 1, Santo Tomas, Batangas',
      contact: '0917-831-8727',
      city: 'Santo Tomas, Batangas',
      address: '448R+GHM, 1',
      isMain: false,
      mapLink: 'https://maps.google.com/?q=Geomatixs+Engineering+Batangas+City',
      photo: `${branchImagesDir}/${encodeURIComponent('sto tomas.jpg')}`
    },
    {
      name: 'Silang Branch',
      location: 'Aguinaldo Hwy, Lalaan 2, Silang, Cavite',
      contact: '0917-129-4372',
      city: 'Silang, Cavite',
      address: 'Aguinaldo Hwy, Lalaan 2',
      isMain: false,
      mapLink: 'https://www.google.com/maps/search/?api=1&query=14.163631444305402%2C120.9548812560274',
      photo: `${branchImagesDir}/silang.jpg`
    },
    {
      name: 'Lima Tech Branch',
      location: 'Purok 3, 199-B Jose Laurel Hwy, Lipa City, 4217 Batangas',
      contact: '0905-328-0257',
      city: 'Lima Tech, Batangas',
      address: 'Lima Technology Center',
      isMain: false,
      mapLink: 'https://maps.google.com/?q=Geomatixs+Engineering+Lima+Technology+Center+Batangas',
      photo: `${branchImagesDir}/lima2.jpg`
    },
    {
      name: 'Banlic Branch',
      location: '65G3+9H5, Calamba, 4027 Laguna',
      contact: '0993-995-3062',
      city: 'Banlic, Laguna',
      address: '65G3+9H5, Calamba',
      isMain: false,
      mapLink: 'https://maps.google.com/?q=Geomatixs+Engineering+Banlic+Laguna',
      photo: `${branchImagesDir}/banlic.jpg`
    },
    {
      name: 'Balayan Branch',
      location: 'Unit 1, Superin Bldg, Brgy Gumamela, Lemery Road, Balayan, 4213 Batangas',
      contact: '0993-136-5108',
      city: 'Balayan, Batangas',
      address: 'Unit 1, Superin Bldg, Brgy Gumamela, Lemery Road',
      isMain: false,
      mapLink: 'https://maps.google.com/?q=Geomatixs+Engineering+Balayan+Batangas',
      photo: `${branchImagesDir}/balayan.jpg`
    }
  ];



  return (
    <div className="home">
      {/* Loading Screen */}
      {isLoading && (
        <div className="loading-screen">
          <div className="loading-content">
            <img 
              src={`${process.env.PUBLIC_URL}/geologo-new.png`} 
              alt="Geomatixs Group Corporation" 
              className="loading-logo"
              onLoad={() => console.log('Image loaded successfully')}
              onError={(e) => {
                console.log('Image failed to load:', e.target.src);
                console.log('Trying fallback...');
                e.target.src = '/geologo.png';
              }}
            />
            <p className="loading-text">Loading Geomatixs...</p>
          </div>
        </div>
      )}

      {/* New Icon Navbar */}
      <div className={`icon-navbar ${showTopNavbar ? 'visible' : 'hidden'}`}>
        {/* Desktop Navigation */}
        <div className="button-container">
          {/* GGC Logo */}
          <button
            type="button"
            className="nav-logo"
            onClick={goToHomeFromLogo}
            aria-label="Go to home"
            title="Home"
          >
            <img 
              src={`${process.env.PUBLIC_URL}/geologo.png`} 
              alt="" 
              className="nav-logo-img"
            />
          </button>
          
          <button 
            className={`button ${currentPage === 'home' && activeSection === 'home' ? 'active' : ''}`}
            onClick={() => {
              setCurrentPage('home');
              setActiveSection('home');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            title="Home"
          >
            Home
          </button>
          
          <button 
            className={`button ${currentPage === 'services' || activeSection === 'services' ? 'active' : ''}`}
            onClick={() => navigateToHomeSection('services', 'services')}
            title="Services"
          >
            Services
          </button>

          <button 
            className={`button ${currentPage === 'projects' || activeSection === 'projects' ? 'active' : ''}`}
            onClick={() => {
              // Always navigate/scroll to the Projects SECTION on Home
              if (currentPage !== 'home') {
                setIsLoading(true);
                setCurrentPage('home');
                setTimeout(() => {
                  setActiveSection('projects');
                  const projectsSection = document.getElementById('projects');
                  if (projectsSection) {
                    projectsSection.scrollIntoView({ behavior: 'smooth' });
                  }
                  setTimeout(() => setIsLoading(false), 500);
                }, 100);
              } else {
                setActiveSection('projects');
                const projectsSection = document.getElementById('projects');
                if (projectsSection) {
                  projectsSection.scrollIntoView({ behavior: 'smooth' });
                }
              }
            }}
            title="Projects"
          >
            Projects
          </button>

          <button 
            className={`button ${activeSection === 'branches' ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              // If we're not on home page, show loading screen and go to home first, then scroll to branches
              if (currentPage !== 'home') {
                setIsLoading(true);
                setCurrentPage('home');
                // Wait a bit for the page to change, then scroll to branches
                setTimeout(() => {
                  setActiveSection('branches');
                  const branchesSection = document.getElementById('branches');
                  if (branchesSection) {
                    branchesSection.scrollIntoView({ behavior: 'smooth' });
                  }
                  // Hide loading screen after navigation is complete
                  setTimeout(() => {
                    setIsLoading(false);
                  }, 500);
                }, 100);
              } else {
                // If already on home page, just scroll to branches
                setActiveSection('branches');
                const branchesSection = document.getElementById('branches');
                if (branchesSection) {
                  branchesSection.scrollIntoView({ behavior: 'smooth' });
                }
              }
            }}
            title="Branches"
          >
            Branches
          </button>

          <button 
            className={`button ${currentPage === 'testing' || activeSection === 'testing' ? 'active' : ''}`}
            onClick={() => navigateToHomeSection('testing', 'testing')}
            title="Testing"
          >
            Testing
          </button>

          <button
            className={`button ${currentPage === 'about' || activeSection === 'about' ? 'active' : ''}`}
            onClick={() => navigateToHomeSection('about', 'about')}
            title="About"
          >
            About
          </button>

        </div>

        {/* Mobile Top Navigation */}
        <div className="mobile-nav-container">
          <button
            type="button"
            className="mobile-logo"
            onClick={goToHomeFromLogo}
            aria-label="Go to home"
            title="Home"
          >
            GEOMATIXS
          </button>
          <button 
            className="mobile-menu-toggle"
            onClick={() => {
              const menu = document.querySelector('.mobile-menu');
              if (menu) {
                menu.classList.toggle('active');
              }
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
            </svg>
          </button>
        </div>

        {/* Mobile Dropdown Menu */}
        <div className="mobile-menu">
          <button 
            className={`mobile-menu-item ${currentPage === 'home' || activeSection === 'home' ? 'active' : ''}`}
            onClick={() => {
              setCurrentPage('home');
              setActiveSection('home');
              window.scrollTo({ top: 0, behavior: 'smooth' });
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
            </svg>
            <span>Home</span>
          </button>
          <button 
            className={`mobile-menu-item ${currentPage === 'services' || activeSection === 'services' ? 'active' : ''}`}
            onClick={() => {
              navigateToHomeSection('services', 'services');
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            <span>Services</span>
          </button>
          <button 
            className={`mobile-menu-item ${currentPage === 'projects' || activeSection === 'projects' ? 'active' : ''}`}
            onClick={() => {
              if (currentPage !== 'home') {
                setCurrentPage('home');
                setTimeout(() => {
                  setActiveSection('projects');
                  const projectsSection = document.getElementById('projects');
                  if (projectsSection) {
                    projectsSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }, 100);
              } else {
                setActiveSection('projects');
                const projectsSection = document.getElementById('projects');
                if (projectsSection) {
                  projectsSection.scrollIntoView({ behavior: 'smooth' });
                }
              }
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
            </svg>
            <span>Projects</span>
          </button>
          <button 
            className={`mobile-menu-item ${activeSection === 'branches' ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              if (currentPage !== 'home') {
                setCurrentPage('home');
                setTimeout(() => {
                  setActiveSection('branches');
                  const branchesSection = document.getElementById('branches');
                  if (branchesSection) {
                    branchesSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }, 100);
              } else {
                setActiveSection('branches');
                const branchesSection = document.getElementById('branches');
                if (branchesSection) {
                  branchesSection.scrollIntoView({ behavior: 'smooth' });
                }
              }
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
            <span>Branches</span>
          </button>
          <button 
            className={`mobile-menu-item ${currentPage === 'testing' || activeSection === 'testing' ? 'active' : ''}`}
            onClick={() => {
              navigateToHomeSection('testing', 'testing');
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 11H7v6h2v-6zm4 0h-2v6h2v-6zm4 0h-2v6h2v-6zm2.5-9H19V1h-2v1H7V1H5v1H4.5C3.67 2 3 2.67 3 3.5v15C3 19.33 3.67 20 4.5 20h15c.83 0 1.5-.67 1.5-1.5v-15C21 2.67 20.33 2 19.5 2z"/>
            </svg>
            <span>Testing</span>
          </button>
          <button
            className={`mobile-menu-item ${currentPage === 'about' || activeSection === 'about' ? 'active' : ''}`}
            onClick={() => {
              navigateToHomeSection('about', 'about');
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
            </svg>
            <span>About</span>
          </button>
        </div>
      </div>



                          {/* Page Navigation */}

       {/* Conditional Page Rendering */}
       {currentPage === 'testing' && (
         <div style={{ width: '100%', minHeight: '100vh', background: '#1a1a1a', paddingTop: '80px' }}>
           <Testing setCurrentPage={setCurrentPage} setActiveSection={setActiveSection} />
         </div>
       )}


       {currentPage === 'projects' && <Projects setCurrentPage={setCurrentPage} setActiveSection={setActiveSection} />}
       {currentPage === 'services' && (
         <Services
           setCurrentPage={setCurrentPage}
           setActiveSection={setActiveSection}
           targetServiceTab={servicesTargetTab}
           onTargetServiceTabApplied={handleTargetServiceTabApplied}
         />
       )}

       {currentPage === 'about' && (
         <About
           scrollToResourcesInitially={aboutScrollToResources}
           onResourcesScrollDone={handleAboutResourcesScrollDone}
         />
       )}

              {/* Hero Section */}
       {currentPage === 'home' && (
         <section id="home" className="hero">
           <div className="hero-background">
                           <video 
                ref={videoRef}
                id="home-bg-video" 
                autoPlay 
                loop 
                muted 
                playsInline 
                preload="auto"
                poster={`${process.env.PUBLIC_URL}/bgphoto.png`}
                webkit-playsinline="true"
                x5-video-player-type="h5"
                x5-video-player-fullscreen="true"
                crossOrigin="anonymous"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0
                }}
                className="video-fade"
                onLoadStart={() => {
                  console.log('Video loading started');
                }}
                onCanPlay={() => {
                  console.log('Video can play');
                  if (videoRef.current && videoRef.current.paused) {
                    safePlayVideo(videoRef.current);
                  }
                }}
                onLoadedData={() => {
                  console.log('Video data loaded');
                  if (videoRef.current && videoRef.current.paused) {
                    safePlayVideo(videoRef.current);
                  }
                }}
                onError={(e) => {
                  console.log('Hero video error:', e);
                  e.target.style.display = 'none';
                  // Add a fallback background
                  e.target.parentElement.style.background = 'linear-gradient(135deg, #FBFBFB 0%, #808080 100%)';
                }}
              >
                {/* Multiple video sources for better mobile compatibility */}
                <source src={`${process.env.PUBLIC_URL}/bgvideo.mp4`} type="video/mp4" />
                <source src={`${process.env.PUBLIC_URL}/bgvideo.webm`} type="video/webm" />
                <source src={`${process.env.PUBLIC_URL}/bgvideo.ogv`} type="video/ogg" />
                {/* Fallback message for browsers that don't support video */}
                Your browser does not support the video tag.
              </video>
             <div className="hero-overlay"></div>
           </div>
           <div className="hero-content">
             <div className="hero-text">
               <h1 className="hero-title home-title-shadow home-fadein home-fadein-1">
                 <span className="geo-text">GEO</span>
                 <span className="matixs-text">MATIXS</span>
                 <span className="group-text"> GROUP CORP.</span>
               </h1>
               <p className="hero-subtitle home-fadein home-fadein-2">Engineering Laboratory for Industrial & Commercial Industry</p>
               <div className="hero-buttons home-fadein home-fadein-3">
                 <a href="#contact" className="hero-btn primary" onClick={openContactModal}>Contact Us</a>
                 <a
                   href="https://www.facebook.com/geomatixs"
                   target="_blank"
                   rel="noopener noreferrer"
                   className="hero-btn hero-facebook-btn"
                   aria-label="Follow Geomatixs on Facebook (opens in new tab)"
                 >
                  <svg
                    className="hero-facebook-btn__icon"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    focusable="false"
                  >
                     <path
                       fill="currentColor"
                       d="M24 12.073C24 5.446 18.627.073 12 .073S0 5.446 0 12.073c0 5.988 4.378 10.952 10.124 11.854v-8.385H7.077v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.952.925-1.952 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                     />
                   </svg>
                   <span className="hero-facebook-btn__label">Facebook</span>
                 </a>
               </div>
             </div>
             <div className="hero-logo">
               <img 
                 src={`${process.env.PUBLIC_URL}/geologo.png`} 
                 alt="Geomatixs Group Logo" 
                 className="hero-logo-img home-logo-effect"
                 style={{
                   width: '260px',
                   height: '260px',
                   objectFit: 'contain',
                   borderRadius: '2rem',
                   border: '4px solid #000000',
                   background: '#fff',
                   boxShadow: '0 8px 40px 0 rgba(255,211,0,0.18), 0 2px 16px 0 rgba(0,0,0,0.18)'
               }}
                 onError={(e) => {
                   console.log('Hero logo error:', e);
                   e.target.style.display = 'none';
                   // Add fallback logo
                   const fallback = document.createElement('div');
                   fallback.innerHTML = '<div style="width: 260px; height: 260px; background: #fff; border-radius: 2rem; border: 4px solid #000000; display: flex; align-items: center; justify-content: center; box-shadow: 0 8px 40px 0 rgba(255,211,0,0.18), 0 2px 16px 0 rgba(0,0,0,0.18);"><span style="color: #000; font-size: 2rem; font-weight: bold; text-align: center;">GEOMATIXS<br/>GROUP<br/>CORP.</span></div>';
                   e.target.parentElement.appendChild(fallback);
                 }}
               />
           </div>
         </div>

          <div className="hero-ticker" role="region" aria-label="Service capabilities">
            <div className="hero-ticker__glow" aria-hidden="true" />
            <div className="hero-ticker__viewport">
              <div className="hero-ticker__track">
                {[0, 1].map((cycle) => (
                  <div key={cycle} className="hero-ticker__chunk" aria-hidden={cycle === 1}>
                    {HERO_TICKER_SERVICES.map((label) => (
                      <span key={`${cycle}-${label}`} className="hero-ticker__item">
                        <span className="hero-ticker__label">{label}</span>
                        <span className="hero-ticker__sep">◆</span>
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
       </section>
       )}

      {/* Services Section */}
      {currentPage === 'home' && (
        <section id="services" className="section services-section section-animate anim-up">
        <div className="container">
          <div className="services-showcase">
            <header className="services-showcase__header home-section-panel">
              <p className="home-section-eyebrow services-showcase__eyebrow">Laboratory &amp; field</p>
              <h2 className="section-title services-showcase__title">Our Services</h2>
              <p className="home-section-lead services-lead">
                Accredited laboratories and field teams for concrete, geotechnical, steel, pile, and NDT — structured workflows, traceable data, and reports you can rely on.
              </p>
            </header>

            <div className="services-hero">
              <div className="services-hero__banner">
                <img
                  src={`${process.env.PUBLIC_URL}/bannerservice.png`}
                  alt="Geomatixs testing services and laboratory work"
                  className="services-banner-img"
                  decoding="async"
                />
                <div className="services-banner-overlay" aria-hidden="true" />
                <div className="services-hero__banner-caption">
                  <span className="services-hero__banner-kicker">Geomatixs Group Corp.</span>
                  <span className="services-hero__banner-headline">Testing you can build on</span>
                </div>
              </div>
            </div>

            <div className="services-grid-shell">
              <div className="services-grid services-bento">
              <article className="service-card">
                <div className="service-card__media">
                  <span className="service-card__index" aria-hidden="true">01</span>
                  <img src={homeServiceHeroSrc('concrete.jpg')} alt="Concrete testing" className="service-img" decoding="async" />
                </div>
                <div className="service-card__body">
                  <h3 className="service-title">Concrete</h3>
                  <p className="service-description">Advanced concrete testing and analysis for structural integrity and compliance.</p>
                  <ServiceCardLearnMoreButton
                    onClick={() => {
                      setServicesTargetTab('Concrete');
                      setCurrentPage('services');
                      setActiveSection('services');
                    }}
                  />
                </div>
              </article>
              <article className="service-card">
                <div className="service-card__media">
                  <span className="service-card__index" aria-hidden="true">02</span>
                  <img src={homeServiceHeroSrc('soil.jpg')} alt="Soil and geotechnical testing" className="service-img" decoding="async" />
                </div>
                <div className="service-card__body">
                  <h3 className="service-title">Soil | Ground Improvement</h3>
                  <p className="service-description">Comprehensive soil testing and ground improvement solutions for safe foundations.</p>
                  <ServiceCardLearnMoreButton
                    onClick={() => {
                      setServicesTargetTab('Soil & Soil Aggregates Testing');
                      setCurrentPage('services');
                      setActiveSection('services');
                    }}
                  />
                </div>
              </article>
              <article className="service-card">
                <div className="service-card__media">
                  <span className="service-card__index" aria-hidden="true">03</span>
                  <img src={homeServiceHeroSrc('steel.jpg')} alt="Steel and rebar testing" className="service-img" decoding="async" />
                </div>
                <div className="service-card__body">
                  <h3 className="service-title">Steel | Pile Testing</h3>
                  <p className="service-description">Steel quality assurance and pile testing for robust and reliable structures.</p>
                  <ServiceCardLearnMoreButton
                    onClick={() => {
                      setServicesTargetTab('Metal');
                      setCurrentPage('services');
                      setActiveSection('services');
                    }}
                  />
                </div>
              </article>
              <article className="service-card">
                <div className="service-card__media">
                  <span className="service-card__index" aria-hidden="true">04</span>
                  <img src={homeServiceHeroSrc('nondes.jpg')} alt="Non-destructive testing on site" className="service-img" decoding="async" />
                </div>
                <div className="service-card__body">
                  <h3 className="service-title">Non-Destructive Testing</h3>
                  <p className="service-description">State-of-the-art non-destructive testing for material and structural evaluation.</p>
                  <ServiceCardLearnMoreButton
                    onClick={() => {
                      setServicesTargetTab('Non-destructive Testing');
                      setCurrentPage('services');
                      setActiveSection('services');
                    }}
                  />
                </div>
              </article>
              </div>
            </div>
          </div>
        </div>
      </section>
        )}

              {/* Our Valued Clients Section */}
        {currentPage === 'home' && (
          <section id="testimonials" className="section testimonials-section section-animate anim-fade">
         <div className="container">
           <h2 className="section-title text-center">Our Valued Clients</h2>
                       <div className="valued-clients-carousel">
              <div className="carousel-track" style={{ transform: `translateX(${carouselPosition}px)` }}>
                                                  {/* Create infinite loop by duplicating cards multiple times */}
                 {[...clientLogos, ...clientLogos, ...clientLogos, ...clientLogos].map((logo, index) => (
                   <div key={index} className="valued-client-card">
                     <div className="client-card">
                       <div className="client-card-inner">
                         <div className="client-card-content">
                           <div className="client-logo-container">
                                                           <img 
                                src={valuedClientLogoSrc(logo)} 
                                alt="Client Logo"
                                className="client-logo-image"
                                loading="lazy"
                                decoding="async"
                                onError={(e) => {
                                  const img = e.currentTarget;
                                  if (img.dataset.clientLogoFallback === '1') return;
                                  img.dataset.clientLogoFallback = '1';
                                  img.alt = '';
                                  img.style.display = 'none';
                                  const parent = img.parentElement;
                                  if (!parent || parent.querySelector('.client-logo-fallback')) return;
                                  const fallback = document.createElement('div');
                                  fallback.className = 'client-logo-fallback';
                                  fallback.setAttribute('role', 'img');
                                  fallback.setAttribute('aria-label', 'Client logo unavailable');
                                  fallback.innerHTML =
                                    '<div style="width: 100%; height: 100%; background: #333; border-radius: 0.5rem; display: flex; align-items: center; justify-content: center; color: #FFD300; font-size: 0.8rem; text-align: center;">Client<br/>Logo</div>';
                                  parent.appendChild(fallback);
                                }}
                              />
                           </div>
                         </div>
                       </div>
                       <div className="client-card-glow"></div>
                     </div>
                   </div>
                 ))}
              </div>
            </div>
         </div>
       </section>
        )}

      {/* Projects Section */}
      {currentPage === 'home' && (
        <section id="projects" className="section projects-section with-bg section-animate anim-up">
        <div className="container projects-content-wrapper">
                     <div className="projects-banner">
             <img src={`${process.env.PUBLIC_URL}/project.png`} alt="Project Banner" className="projects-banner-img" />
          </div>
          <h2 className="section-title">Projects</h2>
          <div className="projects-cta">
                         <button
                           type="button"
                           onClick={() => setCurrentPage('projects')}
                           className="projects-btn btn-animate"
                           title="Open project gallery"
                         >
                           View Our Project Gallery
                         </button>
          </div>
        </div>
      </section>
        )}

      {/* Branches Section */}
      {currentPage === 'home' && (
        <section id="branches" className="section branches-section section-animate anim-stagger">
        <div className="container">
          <header className="home-section-panel branches-intro">
            <p className="home-section-eyebrow">Nationwide laboratories</p>
            <h2 className="section-title">Our Branches</h2>
            <p className="home-section-hint" role="note">
              Map previews use Google Maps. If they stay blank or your console shows blocked requests, a privacy or ad-blocking extension may be stopping Google—use <strong>View on Maps</strong> or allow this site.
            </p>
          </header>
          <div className="branches-grid-shell">
            <div className="branches-grid">
            {branches.map((branch, index) => (
              <div
                key={index}
                className={`branch-card ${branch.isMain ? 'branch-card-main' : ''} ${
                  index % 3 === 0 ? 'branch-pos-left' : index % 3 === 1 ? 'branch-pos-middle' : 'branch-pos-right'
                }`}
                style={{ transitionDelay: `${(index % 3) * 80}ms` }}
              >
                <div className="branch-card__text">
                  <div className="branch-header">
                    <h3 className="branch-name">{branch.name}</h3>
                    {branch.isMain && <span className="main-branch-tag">Main Branch</span>}
                  </div>
                  <div className="branch-meta">
                    <p className="branch-location">{branch.location}</p>
                    <p className="branch-contact">{branch.contact}</p>
                  </div>
                </div>
                <figure className="branch-card__media">
                  <button
                    type="button"
                    className="branch-card__media-trigger"
                    onClick={() =>
                      setBranchPhotoLightbox({
                        src: branch.photo ?? branchPhotoFallback,
                        alt: `Geomatixs ${branch.name} — exterior`,
                        title: branch.name
                      })
                    }
                    aria-label={`Enlarge branch photo, ${branch.name}`}
                  >
                    <img
                      src={branch.photo ?? branchPhotoFallback}
                      alt=""
                      className="branch-card__photo"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        const img = e.currentTarget;
                        if (img.dataset.branchPhotoFallback === '1') return;
                        img.dataset.branchPhotoFallback = '1';
                        img.src = branchPhotoFallback;
                      }}
                    />
                  </button>
                </figure>
                <div className="branch-map-card">
                  <BranchMapIframe
                    title={`Map of ${branch.name}`}
                    src={
                      branch.isMain
                        ? 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3865.764088896863!2d121.0627906760568!3d14.32513138375973!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3397d7ad5ca16703%3A0x1d03e76c11a5701a!2sGeomatixs%20Engineering%20and%20Technical%20Testing%20Services!5e0!3m2!1sen!2sph!4v1757038658425!5m2!1sen!2sph'
                        : branch.name === 'Tanza Branch'
                          ? 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d885.9814038182361!2d120.85331643332493!3d14.388016209265615!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x33962ddb2036a1bd%3A0xcbcaf316062a7682!2sGeomatixs%20Engineering%20and%20Technical%20Testing%20Services!5e0!3m2!1sen!2sph!4v1771395332425!5m2!1sen!2sph'
                          : branch.name === 'Gentri Branch'
                            ? 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d61846.0212637344!2d120.82809874863277!3d14.347619699999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3397d54e4768f0b7%3A0x82296e101cce07e3!2sGeomatixs%20Engineering%20and%20Technical%20Testing%20Services!5e0!3m2!1sen!2sph!4v1757039283280!5m2!1sen!2sph'
                            : branch.name === 'Sto Tomas Branch'
                              ? 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7738.671743729258!2d121.13342310627327!3d14.116334699999992!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x33bd656e361c5abb%3A0x1b4e01a2ae32b15c!2sGeomatixs%20Engineering!5e0!3m2!1sen!2sph!4v1757039402859!5m2!1sen!2sph'
                              : branch.name === 'Silang Branch'
                                ? 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3160.4265493332136!2d120.9548812560274!3d14.163631444305402!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x33bd7f1916db2d09%3A0xaf99ec44648648ec!2sGeomatixs%20Silang!5e0!3m2!1sen!2sph!4v1781829518930!5m2!1sen!2sph'
                                : branch.name === 'Lima Tech Branch'
                                  ? 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d104209.40566172611!2d121.09084034559653!3d13.916560591483732!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x33bd6de8ff0d7497%3A0x8621cfe6951a9612!2sGeomatixs%20Engineering%20and%20Technical%20Testing%20Services!5e0!3m2!1sen!2sph!4v1757040431437!5m2!1sen!2sph'
                                  : branch.name === 'Banlic Branch'
                                    ? 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d574.9028420498578!2d121.15350287171722!3d14.225788045991271!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x33bd6300079b8a93%3A0xd6ec246a018d1aa9!2sGeomatixs%20Engineering%20LGEJV%20Site%20Laboratory!5e0!3m2!1sen!2sph!4v1757039896012!5m2!1sen!2sph'
                                    : branch.name === 'Balayan Branch'
                                      ? 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d123910.87294529028!2d120.61699600037257!3d13.945813399999997!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x33bda3369dd6842d%3A0x8502c97675dead52!2sGeomatixs%20Engineering%20and%20Technical%20Testing%20Services!5e0!3m2!1sen!2sph!4v1757040491939!5m2!1sen!2sph'
                                      : `https://maps.google.com/maps?q=${encodeURIComponent(branch.location)}&t=&z=15&ie=UTF8&iwloc=&output=embed`
                    }
                  />
                  <a href={branch.mapLink} target="_blank" rel="noopener noreferrer" className="branch-map-button">View on Maps</a>
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>
      </section>
        )}

      {/* Testing Section */}
      {currentPage === 'home' && (
        <section id="testing" className="section testing-section section-animate">
        <div className="container">
          <header className="home-section-panel testing-intro">
            <p className="home-section-eyebrow">Field operations</p>
            <h2 className="section-title">In-Site Testing</h2>
            <p className="home-section-lead">
              We offer comprehensive in-site testing services to ensure the quality and safety of your projects, using the latest technology and industry standards.
            </p>
          </header>
          <div className="testing-showcase">
            <div className="testing-showcase__media">
              <div
                className="testing-preview-container"
                tabIndex={0}
                role="group"
                aria-label="Testing preview video. Focus or hover to play a short clip."
                onMouseEnter={(e) => {
                  const vid = e.currentTarget.querySelector('.testing-video-preview');
                  if (vid) safePlayVideo(vid);
                }}
                onMouseLeave={(e) => {
                  const vid = e.currentTarget.querySelector('.testing-video-preview');
                  if (vid) {
                    try {
                      vid.pause();
                    } catch (err) {
                      console.log('Video pause error:', err);
                    }
                  }
                }}
                onFocus={(e) => {
                  const vid = e.currentTarget.querySelector('.testing-video-preview');
                  if (vid) safePlayVideo(vid);
                }}
                onBlur={(e) => {
                  const vid = e.currentTarget.querySelector('.testing-video-preview');
                  if (vid) {
                    try {
                      vid.pause();
                    } catch (err) {
                      console.log('Video pause error:', err);
                    }
                  }
                }}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/bannerservice.png`}
                  alt="Geomatixs testing services preview"
                  className="testing-preview-img"
                  decoding="async"
                />
                <video
                  src={encodeURI(`${process.env.PUBLIC_URL}/videos/COMPRESSIVE TEST.mp4`)}
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="testing-video-preview"
                  aria-hidden="true"
                  onError={(e) => {
                    console.log('Testing video error:', e);
                    e.target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.innerHTML = '<p style="color: #FFD300; text-align: center; padding: 2rem;">Video content available</p>';
                    fallback.style.position = 'absolute';
                    fallback.style.top = '50%';
                    fallback.style.left = '50%';
                    fallback.style.transform = 'translate(-50%, -50%)';
                    e.target.parentElement.appendChild(fallback);
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setCurrentPage('testing');
                  setActiveSection('testing');
                  window.scrollTo(0, 0);
                }}
                className="testing-btn btn-animate testing-showcase__cta"
              >
                View our Testing Videos
              </button>
            </div>
          </div>
        </div>
      </section>
        )}

      {/* Call to Action Section */}
      {currentPage === 'home' && (
        <section id="about" className="section cta-section section-animate">
        <div className="container">
          <div className="about-cta-card">
            <p className="about-cta-card__eyebrow">Who we are</p>
            <h2 className="about-cta-card__title">Want to know us</h2>
            <div className="about-cta-card__rule" aria-hidden="true" />
            <p className="about-cta-card__body">
              Discover our team, certifications, and how we deliver reliable geotechnical and construction
              testing—from the field to your reports.
            </p>
            <div className="about-cta-card__actions">
              <button
                type="button"
                className="cta-btn primary about-cta-card__btn"
                onClick={() => {
                  setCurrentPage('about');
                  setActiveSection('about');
                  window.scrollTo(0, 0);
                }}
              >
                Get started
              </button>
            </div>
          </div>
        </div>
      </section>
        )}

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          {/* Certification Logos */}
          <div className="certification-logos">
            <div className="cert-logo">
              <img src={`${process.env.PUBLIC_URL}/FOOTER/ASTM LOGO.png`} alt="ASTM Member" />
            </div>
            <div className="cert-logo">
              <img src={`${process.env.PUBLIC_URL}/geologo-new.png`} alt="Geomatixs Group Corporation" />
            </div>
            <div className="cert-logo">
              <img src={`${process.env.PUBLIC_URL}/FOOTER/DPWH LOGO.png`} alt="DPWH-BRS ACCR" />
            </div>
            <div className="cert-logo">
              <img src={`${process.env.PUBLIC_URL}/FOOTER/Philgeps.png`} alt="PHILGEPS PLATINUM MEMBER" />
            </div>
          </div>

          {/* Footer Content */}
          <div className="footer-content">
            <div className="footer-column footer-column--brand">
              <h3 className="footer-section-title">Connect</h3>
              <p className="footer-brand-line">
                <svg className="footer-brand-line__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V9h2v2zm0-4h-2V5h2v2z"/>
                </svg>
                Geomatixs Group Corp.
              </p>
              <nav className="footer-links" aria-label="Social and email">
                <a href="https://www.facebook.com/geomatixs" target="_blank" rel="noopener noreferrer" className="footer-link">
                  <svg className="footer-link__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 6.11 14.22 6.11C15.31 6.11 16.63 6.29 16.63 6.29V8.62H15.25C13.94 8.62 13.44 9.39 13.44 10.39V12.06H16.42L15.84 14.96H13.44V21.96A10 10 0 0 0 22 12.06C22 6.53 17.5 2.04 12 2.04Z"/>
                  </svg>
                  Facebook
                </a>
                <a href="mailto:geomatixs.information@gmail.com" className="footer-link">
                  <svg className="footer-link__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                  Email
                </a>
              </nav>
            </div>

            <div className="footer-column">
              <h3 className="footer-section-title">Company</h3>
              <nav className="footer-links" aria-label="Company links">
                <button
                  type="button"
                  className="footer-link"
                  onClick={() => {
                    setCurrentPage('about');
                    setActiveSection('about');
                    window.scrollTo(0, 0);
                  }}
                >
                  About Us
                </button>
                <a href="#projects" className="footer-link">Projects</a>
                <a href="#branches" className="footer-link">Branches</a>
                <a href="#testing" className="footer-link">Testing</a>
                <a href="mailto:careers@geomatixs.com" className="footer-link">Careers</a>
              </nav>
            </div>

            <div className="footer-column footer-column--contact">
              <h3 className="footer-section-title">Contact</h3>
              <div className="footer-contact">
                <div className="contact-item">
                  <svg className="footer-link__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                  <span>Unit 24 Paseo de Carmona Bldg. Brgy, Carmona, Cavite</span>
                </div>
                <div className="contact-item">
                  <svg className="footer-link__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                  </svg>
                  <span>0977 292 4357</span>
                </div>
                <div className="contact-item">
                  <svg className="footer-link__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                  <span>geomatixs.engtest@gmail.com</span>
                </div>
              </div>
            </div>
          </div>

          {/* Separator Line */}
          <div className="footer-separator"></div>

          {/* Copyright */}
          <div className="footer-bottom">
            <p>&copy; 2020 Geomatixs Group Corp. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Floating Contact Button */}
      <button
        id="contact-fab"
        className="floating-contact-btn"
        onClick={openContactModal}
        aria-label="Contact Us"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 32 32" width="28" height="28">
          <circle cx="16" cy="16" r="16" fill="#FFD300"/>
          <path d="M8 12l8 6 8-6" stroke="#1A1A1A" strokeWidth="2" fill="none"/>
          <rect x="8" y="10" width="16" height="12" rx="2" stroke="#1A1A1A" strokeWidth="2" fill="none"/>
        </svg>
      </button>

      {/* Scroll Progress Indicator */}
      <div id="scroll-progress-container" className="scroll-progress-container">
        <svg className="scroll-progress-svg" viewBox="0 0 40 40">
          <circle
            ref={scrollProgressRef}
            className="scroll-progress-circle"
            cx="20"
            cy="20"
            r="18"
            strokeDasharray="125.6"
            strokeDashoffset="125.6"
          />
        </svg>
        <button
          className="scroll-up-btn"
          onClick={scrollToTop}
          style={{ opacity: showScrollTop ? 1 : 0, pointerEvents: showScrollTop ? 'auto' : 'none' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      </div>

      {branchPhotoLightbox && (
        <div
          className="branch-photo-lightbox"
          role="dialog"
          aria-modal="true"
          aria-labelledby="branch-photo-lightbox-title"
        >
          <button
            type="button"
            className="branch-photo-lightbox__scrim"
            onClick={() => setBranchPhotoLightbox(null)}
            aria-label="Close enlarged photo"
          />
          <div className="branch-photo-lightbox__panel">
            <button
              type="button"
              className="branch-photo-lightbox__close"
              onClick={() => setBranchPhotoLightbox(null)}
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <p id="branch-photo-lightbox-title" className="branch-photo-lightbox__title">
              {branchPhotoLightbox.title}
            </p>
            <div className="branch-photo-lightbox__img-wrap">
              <img src={branchPhotoLightbox.src} alt={branchPhotoLightbox.alt} className="branch-photo-lightbox__img" />
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      {contactModalOpen && (
        <div id="contact-modal" className="contact-modal open" onClick={handleModalBackdropClick}>
          <div className="contact-modal-content">
            <div className="contact-modal-header">
              <h3>Contact Us</h3>
              <button className="contact-modal-close" onClick={closeContactModal}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form className="contact-form" onSubmit={handleContactSubmit}>
              <div className="form-group">
                <label htmlFor="modal-name">Name</label>
                <input 
                  type="text" 
                  id="modal-name" 
                  name="name" 
                  required 
                  value={formData.name} 
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.name ? 'error' : ''} ${formData.name ? 'success' : ''}`}
                />
                {formErrors.name && <p className="form-error">{formErrors.name}</p>}
              </div>
              <div className="form-group">
                <label htmlFor="modal-email">Email</label>
                <input 
                  type="email" 
                  id="modal-email" 
                  name="email" 
                  required 
                  value={formData.email} 
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.email ? 'error' : ''} ${formData.email ? 'success' : ''}`}
                />
                {formErrors.email && <p className="form-error">{formErrors.email}</p>}
              </div>
              <div className="form-group">
                <label htmlFor="mobile">Mobile Number *</label>
                <input
                  type="tel"
                  id="mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.mobile ? 'error' : ''} ${formData.mobile ? 'success' : ''}`}
                  placeholder="09XXXXXXXXX"
                  required
                />
                {formErrors.mobile && (
                  <div className="notifications-container">
                    <div className="error-alert">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg aria-hidden="true" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" className="error-svg">
                            <path clipRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" fillRule="evenodd"></path>
                          </svg>
                        </div>
                        <div className="error-prompt-container">
                          <p className="error-prompt-heading">Invalid mobile number format</p>
                          <div className="error-prompt-wrap">
                            <ul className="error-prompt-list">
                              <li>Mobile number must start with "09"</li>
                              <li>Mobile number must be exactly 11 digits</li>
                              <li>Only numbers are allowed (no spaces or special characters)</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="modal-message">Message</label>
                <textarea 
                  id="modal-message" 
                  name="message" 
                  rows="4" 
                  required 
                  value={formData.message} 
                  onChange={handleInputChange}
                  className={`form-input form-textarea ${formErrors.message ? 'error' : ''} ${formData.message ? 'success' : ''}`}
                />
                {formErrors.message && <p className="form-error">{formErrors.message}</p>}
              </div>
              <button type="submit" className="submit-btn" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
              {submitMessage && (
                <div className={`submit-message ${submitMessage.includes('successfully') ? 'success' : 'error'}`}>
                  {submitMessage}
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
